import React from "react";
import { useNavigate } from "react-router-dom";
import baseUrl from "../../api/baseUrl";

type CheckoutData = {
  orderId: string;
  amount: string;
  currency?: string;
  hash: string;
  merchant_id: string;
  packageName: string;
  tokens: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  address?: string;
  clinicId: string;
  purchaseId: string;
};

async function cancelPendingPurchase(purchaseId: string, reason: "dismissed" | "error") {
  try {
    await baseUrl.post(`/client/sms-packages/purchases/${purchaseId}/cancel`, { reason });
  } catch {
    // Ignore — stale pending rows are also cleaned up server-side
  }
}

export default function SmsPayButton({
  checkout,
  disabled,
  onPaying,
  onPaymentEnded,
}: {
  checkout: CheckoutData | null;
  disabled?: boolean;
  onPaying?: () => void;
  onPaymentEnded?: () => void;
}) {
  const navigate = useNavigate();

  const handlePayment = () => {
    if (!checkout?.hash || !checkout?.orderId) return;
    onPaying?.();

    const payment = {
      merchant_id: checkout.merchant_id,
      return_url: undefined,
      cancel_url: undefined,
      notify_url: `${process.env.REACT_APP_API}/client/sms-packages/payhere/notify`,
      order_id: checkout.orderId,
      items: `${checkout.packageName} (${checkout.tokens} SMS tokens)`,
      amount: checkout.amount,
      currency: checkout.currency || "LKR",
      first_name: checkout.firstName || "Clinic",
      last_name: checkout.lastName || "User",
      email: checkout.email || "clinic@example.com",
      phone: checkout.phone || "0770000000",
      address: checkout.address || "Colombo",
      city: "Colombo",
      country: "Sri Lanka",
      hash: checkout.hash,
      custom_1: checkout.purchaseId,
      custom_2: checkout.clinicId,
    };

    const ph = window.payhere;
    if (!ph) {
      console.error("PayHere SDK not loaded");
      return;
    }

    const finish = async (path: string, reason: "dismissed" | "error") => {
      if (checkout.purchaseId) {
        await cancelPendingPurchase(checkout.purchaseId, reason);
      }
      onPaymentEnded?.();
      navigate(path);
    };

    ph.startPayment(payment);

    ph.onCompleted = function onCompleted() {
      onPaymentEnded?.();
      navigate("/client/payment/success");
    };
    ph.onDismissed = function onDismissed() {
      void finish("/client/payment/cancel", "dismissed");
    };
    ph.onError = function onError() {
      void finish("/client/payment/cancel", "error");
    };
  };

  return (
    <button
      type="button"
      onClick={handlePayment}
      disabled={disabled || !checkout}
      className="inline-flex h-9 items-center justify-center rounded-lg bg-primary px-4 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
    >
      Pay with card
    </button>
  );
}
